/**
 * detectKey — iter-253
 *
 * Estimates the musical key of an AudioBuffer via:
 *   1. Slice a representative window from the middle of the track
 *      (skips intros + fade-outs which usually carry less harmonic
 *      information).
 *   2. Decimate to ~8 kHz so the per-frequency Goertzel passes are
 *      cheap enough to run inline on the main thread.
 *   3. Compute a 12-bin chromagram by summing the Goertzel magnitude
 *      at each pitch class across multiple octaves (C2 → C6 covers
 *      vocals + most instrument fundamentals).
 *   4. Correlate the chromagram against the Krumhansl-Schmuckler
 *      major + minor key profiles for all 24 candidate keys; the
 *      best Pearson correlation wins.
 *
 * Output: `{ key: "C major" | "A minor" | …, confidence: 0..1 }` or
 * `null` for invalid / sub-second buffers. Confidence ≥ 0.6 is
 * typically a confident match; below that the result is a hint at
 * best.
 *
 * Algorithm references:
 *   - Krumhansl, C. L. & Kessler, E. J. (1982) — Tracing the dynamic
 *     changes in perceived tonal organization (key profiles).
 *   - Generalized Goertzel — works with non-integer DFT bins, no FFT
 *     library needed.
 */

const PITCH_NAMES = ["C", "C#", "D", "D#", "E", "F", "F#", "G", "G#", "A", "A#", "B"];

// Krumhansl-Schmuckler major/minor key profiles (canonical values).
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.60, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];

/**
 * Compute the magnitude of `samples` at `targetFreq` (Hz) using the
 * generalized Goertzel algorithm. O(N) per frequency, no FFT.
 */
function goertzelMagnitude(samples, sampleRate, targetFreq) {
  const w = (2 * Math.PI * targetFreq) / sampleRate;
  const coeff = 2 * Math.cos(w);
  let q1 = 0;
  let q2 = 0;
  for (let i = 0; i < samples.length; i++) {
    const q0 = samples[i] + coeff * q1 - q2;
    q2 = q1;
    q1 = q0;
  }
  const magSq = q1 * q1 + q2 * q2 - q1 * q2 * coeff;
  return magSq > 0 ? Math.sqrt(magSq) : 0;
}

/**
 * Pearson correlation between the chroma (rotated by `shift`) and the
 * 12-element profile. Used to score each candidate key.
 */
function pearsonCorrelation(chroma, profile, shift) {
  const shifted = new Array(12);
  for (let i = 0; i < 12; i++) shifted[i] = chroma[(i + shift) % 12];
  let xMean = 0;
  let yMean = 0;
  for (let i = 0; i < 12; i++) { xMean += shifted[i]; yMean += profile[i]; }
  xMean /= 12;
  yMean /= 12;
  let num = 0;
  let dx = 0;
  let dy = 0;
  for (let i = 0; i < 12; i++) {
    const xi = shifted[i] - xMean;
    const yi = profile[i] - yMean;
    num += xi * yi;
    dx += xi * xi;
    dy += yi * yi;
  }
  return dx > 0 && dy > 0 ? num / Math.sqrt(dx * dy) : 0;
}

/**
 * Decimate `samples` to a target sample rate via simple averaging
 * (avoids needing a proper low-pass filter — the chromagram only cares
 * about energy in narrow bins, aliasing in the wide-band signal is a
 * non-issue for that).
 */
function decimate(samples, srcRate, targetRate) {
  if (srcRate <= targetRate) return { samples, rate: srcRate };
  const factor = Math.floor(srcRate / targetRate);
  if (factor <= 1) return { samples, rate: srcRate };
  const outLen = Math.floor(samples.length / factor);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    let acc = 0;
    for (let j = 0; j < factor; j++) acc += samples[i * factor + j];
    out[i] = acc / factor;
  }
  return { samples: out, rate: srcRate / factor };
}

/**
 * Public API — estimate the key of an AudioBuffer-like object.
 *
 * `buffer` must expose `duration`, `sampleRate`, and
 * `getChannelData(idx) → Float32Array`.
 */
export function detectKey(buffer, opts = {}) {
  if (!buffer || typeof buffer.getChannelData !== "function") return null;
  const sr = buffer.sampleRate;
  if (!sr || sr <= 0) return null;
  const duration = buffer.duration;
  if (!duration || duration < 1) return null;

  const { sampleSec = 10, startOffsetSec = null } = opts;
  // Default: take a window from 25% into the track (skips intros) for up to
  // `sampleSec` seconds. Cap to the track length.
  const startSec = startOffsetSec != null
    ? Math.max(0, Math.min(duration - 1, startOffsetSec))
    : Math.max(0, duration * 0.25);
  const endSec = Math.min(duration, startSec + sampleSec);
  if (endSec - startSec < 1) return null;

  const channel = buffer.getChannelData(0);
  if (!channel || channel.length === 0) return null;
  const startIdx = Math.floor(startSec * sr);
  const endIdx = Math.floor(endSec * sr);
  const slice = channel.subarray(startIdx, endIdx);

  // Decimate to ~8 kHz so the per-frequency Goertzel sweep is cheap.
  const { samples: dsSamples, rate: dsRate } = decimate(slice, sr, 8000);

  // Chromagram — sum Goertzel magnitudes across pitch classes × octaves.
  // C2 (~65 Hz) through C6 (~1046 Hz) covers vocals + most instrument
  // fundamentals; higher octaves are mostly harmonics already captured.
  const chroma = new Array(12).fill(0);
  for (let oct = 2; oct <= 5; oct++) {
    for (let pc = 0; pc < 12; pc++) {
      const midi = (oct + 1) * 12 + pc;
      const freq = 440 * Math.pow(2, (midi - 69) / 12);
      chroma[pc] += goertzelMagnitude(dsSamples, dsRate, freq);
    }
  }

  // Normalise so the correlation scores are comparable across tracks.
  const sum = chroma.reduce((s, v) => s + v, 0);
  if (sum <= 0) return null;
  for (let i = 0; i < 12; i++) chroma[i] /= sum;

  // Score every candidate key (12 major + 12 minor) and keep the best.
  let bestKey = null;
  let bestScore = -Infinity;
  let bestRoot = 0;
  let bestQuality = "major";
  for (let i = 0; i < 12; i++) {
    const major = pearsonCorrelation(chroma, MAJOR_PROFILE, i);
    if (major > bestScore) { bestScore = major; bestKey = `${PITCH_NAMES[i]} major`; bestRoot = i; bestQuality = "major"; }
    const minor = pearsonCorrelation(chroma, MINOR_PROFILE, i);
    if (minor > bestScore) { bestScore = minor; bestKey = `${PITCH_NAMES[i]} minor`; bestRoot = i; bestQuality = "minor"; }
  }

  return {
    key: bestKey,
    root: PITCH_NAMES[bestRoot],
    quality: bestQuality,
    confidence: Math.max(0, Math.min(1, bestScore)),
  };
}

// Internal helpers exported for testing only.
export const __internals = { goertzelMagnitude, pearsonCorrelation, decimate, MAJOR_PROFILE, MINOR_PROFILE, PITCH_NAMES };

export default detectKey;
